import React, { useState } from "react"
import { Tabs, Tooltip, Switch } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import {
  faCopy,
  faFileExcel,
  faFileArrowDown,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import BarChart from "shared/ui/Charts/BarChart.component"
import {
  convertDataToTable,
  BarChartDataItem,
  TransformedTableData,
} from "utils/convertDataToTable"
import { exportToExcel } from "utils/exportToExcel"
import { downloadJSON } from "utils/files/downloadUtils"
import { jsonPretty } from "utils/jsonPretty"
import ReportingTable from "shared/ui/Table/ReportingTable.component"
import { EngineOutputItem, RTableContent } from "types/engineOutput.types"
import { STATISTIC_TYPE } from "types/ReportingDashboard/elements.types"

// Define the props interface for the OutputTabs component
interface OutputTabsProps {
  /** The result object containing the data and other information */
  result: {
    output?: EngineOutputItem[]
    log?: string[]
  }
}

// Define the structure for chart data expected by BarChart
interface ChartData {
  data: BarChartDataItem[]
  keys: string[]
  statisticTypes: STATISTIC_TYPE[]
  xAxisLabel: string
}

// Define the structure for tab items
interface TabItem {
  key: string
  label: string
  children: React.ReactNode
}

/**
 * OutputTabs component to render tabbed output from a result object.
 * It supports rendering data as tables, charts, or JSON, and includes options to copy, export, or download the data.
 *
 * @param props - The component props
 * @returns {JSX.Element|null} A component that renders the result in tabbed format, or null if no result is available.
 */
const OutputTabs: React.FC<OutputTabsProps> = ({ result }) => {
  const [chartActive, setChartActive] = useState<boolean>(false)

  if (!result || !result.log) {
    return null
  }

  if (result.output === undefined || result.output.length === 0) {
    return (
      <p className="p-2">
        No Output in result. Please check if query has output. See log below
      </p>
    )
  }

  /**
   * Copies the given content to the clipboard and displays a success message.
   *
   * @param content - The content to copy to the clipboard.
   */
  const CopyToClipboard = (content: unknown): void => {
    const contentToCopy = jsonPretty(content)
    navigator.clipboard.writeText(contentToCopy)
    notify.toast.success("Output copied to clipboard")
  }

  const tabItems: TabItem[] = result.output.map((item, index) => {
    // Type guard to check if content is RTableContent
    const isRTableContent = (content: unknown): content is RTableContent => {
      return (
        typeof content === "object" &&
        content !== null &&
        "data" in content &&
        Array.isArray((content as RTableContent).data)
      )
    }

    const isRtable = item.format !== "json" && isRTableContent(item.content)
    let content: string | TransformedTableData

    if (item.format === "text") {
      content =
        typeof item.content === "string" ? item.content : String(item.content)
    } else {
      content = isRtable
        ? convertDataToTable(item.content as RTableContent)
        : jsonPretty(item.content)
    }

    const contentTooLong =
      !isRtable && typeof content === "string" && content.length > 1000000

    // Extract chart data for bar chart rendering
    const chartData: ChartData | null =
      isRtable && typeof content === "object" && content.barChartData
        ? {
            data: content.barChartData.data as BarChartDataItem[],
            keys: content.barChartData.keys,
            statisticTypes: content.barChartData
              .statisticTypes as STATISTIC_TYPE[],
            xAxisLabel: content.barChartData.xAxisLabel,
          }
        : null

    return {
      key: `${index + 1}`,
      label: item.label || `Table ${index + 1}`,
      children: (
        <div className="flex flex-col w-full h-full">
          <div className="flex justify-between items-center p-3 shadow-sm">
            <div className="flex items-center space-x-4">
              {isRtable && (
                <>
                  <Switch
                    style={{ backgroundColor: "#36c3ed" }}
                    checkedChildren="Chart"
                    unCheckedChildren="Table"
                    checked={chartActive}
                    onChange={(checked: boolean) => setChartActive(checked)}
                  />
                </>
              )}
            </div>
            <div className="flex space-x-3">
              {result.output && result.output.length >= 1 && (
                <Tooltip title="Download All Outputs">
                  <FontAwesomeIcon
                    icon={faFileArrowDown}
                    color="#36c3ed"
                    className="cursor-pointer"
                    onClick={() => downloadJSON(result.output!, `Output`)}
                  />
                </Tooltip>
              )}
              <Tooltip title="Copy to clipboard">
                <FontAwesomeIcon
                  icon={faCopy}
                  color="#36c3ed"
                  className="cursor-pointer"
                  onClick={() => CopyToClipboard(item.content)}
                />
              </Tooltip>
              {isRtable && (
                <Tooltip title="Export to Excel">
                  <FontAwesomeIcon
                    icon={faFileExcel}
                    color="#36c3ed"
                    className="cursor-pointer"
                    onClick={async () => {
                      const rTableContent = item.content as RTableContent
                      await exportToExcel(
                        rTableContent.data,
                        item.label || `Table_${index + 1}`
                      )
                    }}
                  />
                </Tooltip>
              )}
            </div>
          </div>
          <div className="h-full overflow-auto px-2">
            {item.format === "text" ? (
              <pre className="m-0 p-0">{content as string}</pre>
            ) : isRtable ? (
              chartActive && chartData ? (
                <BarChart
                  isChart={!(content as TransformedTableData).hasSubHeaders}
                  data={chartData.data}
                  keys={chartData.keys}
                  statisticTypes={chartData.statisticTypes}
                  xAxisLabel={chartData.xAxisLabel}
                />
              ) : (
                <ReportingTable
                  transformedData={content as TransformedTableData}
                />
              )
            ) : contentTooLong ? (
              <p className="p-2">
                Content is too large to display. Click{" "}
                <a
                  className="text-blue-500 cursor-pointer"
                  onClick={() =>
                    downloadJSON(
                      item.content,
                      item.label || `Output_${index + 1}`
                    )
                  }
                >
                  here
                </a>{" "}
                to download the full content.
              </p>
            ) : (
              <pre className="m-0 p-0">{content as string}</pre>
            )}
          </div>
        </div>
      ),
    }
  })

  return (
    <div className="h-full w-full overflow-auto">
      <Tabs
        defaultActiveKey="1"
        items={tabItems}
        className="data-reporting-tabs"
      />
    </div>
  )
}

export default OutputTabs
